/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {useEffect, useState} from 'react';
import {styled, useTheme} from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {Box, Card as MuiCard, Grid} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  CrossOutlined,
  Categorize,
  LeftArrow,
} from '@jobseeker/lotus/assets/icons';
import JobOverviewCard from '@jobseeker/lotus/components/JobOverviewCard';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {FaChevronRight as ChevronRight} from 'react-icons/fa6';
import {Chart} from 'react-chartjs-2';
import {useWidth, useIsLargeDevice} from '@jobseeker/lotus/hooks';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import {useSelector} from 'react-redux';
import kebabCase from 'lodash/kebabCase';
import moment from 'moment';
import {Insights} from '@jobseeker/lotus/assets/images';
import {getOldLink, getProLink} from '@jobseeker/lotus/utils';

import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';

import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  // Legend,
  Tooltip,
  LineController,
  BarController,
} from 'chart.js';
import {InsightsModalProps} from '../type';

const isAppIIMJobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  // Legend,
  Tooltip,
  LineController,
  BarController
);

const Gradient = `linear-gradient(243deg, rgba(215, 220, 210, 0.96) 1.33%, rgba(215, 220, 210, 0.96) 62.26%, 
rgba(233, 222, 197, 0.96) 87.65%, rgba(215, 195, 226, 0.96) 100.76%, rgba(255, 255, 255, 0.00) 100.77%)`;

const Card = styled(MuiCard)(({theme}) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
}));

const Tab = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  color: theme.palette.text.secondary,
  alignItems: 'center',
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    border: '1px solid',
    borderColor: '#fff',
  },
  '.icon': {
    fontSize: '12px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  '&.active': {
    backgroundColor: isAppIIMJobs ? theme.palette.text.primary : '#13355c',
    color: '#fff',
    p: {
      fontWeight: 500,
    },
  },
}));
const BootstrapDialog = styled(Dialog)(({theme}) => ({
  '.MuiDialog-paper': {
    backgroundColor: theme.palette.border.main,
    borderRadius: theme.spacing(2),
  },
  [theme.breakpoints.down('md')]: {
    '.MuiDialog-paper': {
      borderRadius: theme.spacing(0),
    },
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const TabMap = {
  competition: {
    first: 'competitionData',
    second: 'competitionDataRecruiterActions',
    label: 'Competition Data',
    heading: 'DISTRIBUTION OF APPLICATIONS BY EXPERIENCE',
  },
  applied: {
    first: 'appliedData',
    second: 'appliedDataRecruiterActions',
    label: 'Applied Data',
    heading: 'DISTRIBUTION OF JOB APPLICATIONS & RECRUITER ACTIONS',
  },
  views: {
    first: 'viewData',
    second: 'viewDataRecruiterActions',
    label: 'View Data',
    heading: 'DISTRIBUTION OF JOB VIEWS & RECRUITER ACTIONS',
  },
};

const NoInsightsMessage = ({isProUser}: {isProUser: boolean}) => {
  if (isProUser) {
    return (
      <Box>
        <Typography
          fontSize={{xxs: 16}}
          textAlign={'center'}
          fontWeight={500}
          lineHeight={'24px'}
        >
          Recent Job Posting
        </Typography>
        <Typography
          fontSize={{xxs: 14}}
          fontWeight={500}
          textAlign={'center'}
          lineHeight={'21px'}
        >
          No insights available at the moment. Please check later
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      fontSize={{xxs: 20, md: 24, lg: 28}}
      fontWeight={500}
      textAlign={'center'}
    >
      Want to see if you have
      <br />
      an edge over other Applicants?
    </Typography>
  );
};

const Component = (props: InsightsModalProps) => {
  const handleClose = (event: any) => {
    if (props.onClose) props.onClose(event, 'escapeKeyDown');
    if (props.handleClose) props.handleClose(event, 'escapeKeyDown');
  };
  const [activeTab, setActiveTab] = useState<
    'competition' | 'applied' | 'views'
  >('competition');
  const [chartData, setChartData] = useState<any>(undefined);
  const [insightsAvailable, setInsightsAvailable] = useState<boolean>(true);

  const isLarge = useIsLargeDevice();
  const isLargeXL = useIsLargeDevice(470);
  const theme = useTheme();
  const width = useWidth();
  const widthMap: any = {
    xxs: '310px',
    md: '150px',
    lg: '120px',
  };

  const isProUser = props?.proUser;

  ChartJS.defaults.font.size = isLarge ? 12 : 8;

  // const [value, setValue] = useState(0);
  const jobDetail: JB = useSelector((state: any) => state.jobDetail);

  // const jobDetailInsights = useSelector(
  //   (state: AppState) => state.jobDetail.jobDetailInsights,
  //   shallowEqual
  // );

  const jobDetailInsights = jobDetail?.jobDetailInsights;

  // const [chartKey, setChartKey] = useState<any>({});
  // const [chartLabels, setChartLabels] = useState<string[]>([]);
  useEffect(() => {
    if (jobDetailInsights?.competitionData) {
      const valuesOne = Object.values(
        jobDetailInsights[TabMap[activeTab].first]
      ).map((v: any) => parseInt(v));
      const valuesTwo = Object.values(
        jobDetailInsights[TabMap[activeTab].second]
      ).map((v: any) => parseInt(v));
      const xAxisCommon = {
        grid: {
          drawOnChartArea: false,
        },
      };
      const xAxis = {
        title: {
          display: true,
          text: 'Experience',
        },
        ...xAxisCommon,
      };

      setChartData({
        labels: Object.keys(jobDetailInsights[TabMap[activeTab].first]),
        datasets: [
          {
            type: 'line' as const,
            label: 'Recruiter Actions',
            borderColor: theme.palette.gray.light,
            pointBackgroundColor: '#CFFDF1',
            pointBorderColor: '#8DD1C0',
            borderWidth: 2,
            fill: false,
            data: valuesTwo,
          },
          {
            type: 'bar' as const,
            label: activeTab === 'views' ? 'View' : 'Applications',
            backgroundColor: isAppIIMJobs
              ? theme.palette.primary.dark
              : theme.palette.insights,
            data: valuesOne,
            maxBarThickness: 50,
          },
        ],
        options: {
          scales: {
            y: {
              suggestedMin: 0,
              suggestedMax: Math.round(
                Math.max(...valuesOne.concat(valuesTwo)) * 2
              ),
              title: {
                display: true,
                text: 'No. of Candidates',
              },
              ticks: {
                beginAtZero: true,
                callback: (label: number, index: number, labels: number[]) => {
                  if (Math.floor(label) === label) {
                    return label;
                  }
                },
              },
            },
            x: ['competition'].includes(activeTab) ? xAxis : xAxisCommon,
          },
        },
        heading: TabMap[activeTab].heading,
      });
    }
  }, [activeTab, jobDetailInsights]);

  useEffect(() => {
    const tabData = TabMap[activeTab];
    const data = jobDetailInsights[tabData.first];
    const total = getArraySum(Object.values(data) || []);
    setInsightsAvailable(total >= 1 && isProUser);
  }, [activeTab, jobDetailInsights]);

  const getArraySum = (arr: any[]) => {
    return arr ? arr.reduce((partialSum: any, a: any) => partialSum + a, 0) : 0;
  };
  return (
    <div>
      <BootstrapDialog
        {...props}
        fullWidth
        maxWidth={'lg'}
        fullScreen={!isLarge}
      >
        <Box
          sx={{
            py: 0,
            // overflow: 'hidden',
            position: 'relative',
            pr: 2,
            boxShadow: '0px 6.48px 6px 0px rgba(41, 41, 41, 0.04)',
            backgroundColor: '#fff',
            borderRadius: 4,
          }}
        >
          <DialogTitle pl={{xxs: 7, md: 3}}>
            <>
              <Typography color="text.primary" variant="subtitle1">
                {jobDetail?.title?.split('(')[0]?.trim()}
              </Typography>
              <Typography
                mt={1}
                component={'p'}
                // lineHeight={'16px'}
                color="text.secondary"
                variant="body3"
              >
                {/* {`Originally Posted on ${moment(
                  jobDetail?.createdTimeMs
                ).format('DD-MM-YY')}`} */}
                {!!jobDetailInsights.originallyPosted
                  ? `Originally Posted on ${jobDetailInsights.originallyPosted}`
                  : ''}
                {!!jobDetailInsights.lastRefreshed ? (
                  <>
                    {isLargeXL ? ' | ' : <br />}
                    {`Last Refreshed on ${jobDetailInsights.lastRefreshed}`}
                  </>
                ) : (
                  ''
                )}
              </Typography>
            </>
          </DialogTitle>
          <Box
            sx={{
              position: 'absolute',
              right: {xxs: 'unset', md: 8},
              left: {xxs: 8, md: 'unset'},
              zIndex: 1,
              top: {xxs: -10, md: 0},
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconButton aria-label="close" onClick={handleClose}>
              <Image
                width={24}
                height={24}
                src={isLarge ? CrossOutlined : LeftArrow}
                alt={'cross'}
                // layout="fill"
                // objectFit="cover"
              />
            </IconButton>
          </Box>
        </Box>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xxs={12} md={4}>
              <Card>
                <Box
                  display={'flex'}
                  gap={1}
                  justifyContent={isLarge ? 'flex-start' : 'center'}
                  alignItems={'center'}
                >
                  {isAppIIMJobs ? (
                    <Image
                      src={Categorize}
                      alt="Insights_Logo"
                      width={16}
                      height={16}
                      layout="responsive"
                      objectFit="cover"
                      style={{
                        maxWidth: 16,
                      }}
                    />
                  ) : (
                    <></>
                  )}
                  <Typography variant="subtitle2">INSIGHTS</Typography>
                </Box>
                <Box
                  mt={2}
                  display={isLarge ? 'block' : 'flex'}
                  flexWrap={{xxs: 'wrap', xs: 'nowrap'}}
                  gap={0.5}
                >
                  <Tab
                    className={activeTab === 'competition' ? 'active' : ''}
                    onClick={() => setActiveTab('competition')}
                  >
                    <Typography variant="body2" fontSize={{xxs: 12, md: 14}}>
                      Competition
                    </Typography>
                    <ChevronRight className="icon" />
                  </Tab>
                  <Tab
                    className={activeTab === 'applied' ? 'active' : ''}
                    onClick={() => setActiveTab('applied')}
                  >
                    <Typography variant="body2" fontSize={{xxs: 12, md: 14}}>
                      Applied
                    </Typography>
                    <ChevronRight className="icon" />
                  </Tab>
                  <Tab
                    className={activeTab === 'views' ? 'active' : ''}
                    onClick={() => setActiveTab('views')}
                  >
                    <Typography variant="body2" fontSize={{xxs: 12, md: 14}}>
                      Views
                    </Typography>
                    <ChevronRight className="icon" />
                  </Tab>
                </Box>
              </Card>
              {isLarge && (
                <Card sx={{pt: 3, pb: 2, mt: 1}}>
                  <JobOverviewCard
                    variant={VersionVariants.VERSION_TWO}
                    actionUrl={
                      !jobDetail?.confidential
                        ? getOldLink(
                            `/r/${jobDetail?.recruiter
                              ?.recruiterId}-${kebabCase(
                              jobDetail?.recruiter?.recruiterName.replace(
                                /&/g,
                                '-and-'
                              )
                            )}`
                          )
                        : '#'
                    }
                    confidential={jobDetail?.confidential === 1}
                    setOpenConfJobDialog={props?.setOpenConfJobDialog}
                    recruiterImgUrl={
                      !jobDetail?.confidential
                        ? jobDetail?.recruiter?.profilePicUrl
                        : `${getOldLink('/resources/img/user.jpg')}`
                    }
                    lastActive={
                      jobDetail?.recruiter?.lastActive &&
                      jobDetail?.confidential !== 1
                        ? moment(jobDetail.recruiter.lastActive).isValid()
                          ? moment(jobDetail.recruiter.lastActive).format(
                              'DD MMMM YYYY'
                            )
                          : jobDetail.recruiter.lastActive
                        : ''
                    }
                    jobId={jobDetail?.id}
                    jobViews={jobDetailInsights?.totalViews || jobDetail?.hits}
                    applications={
                      jobDetail?.applicationCount ?? jobDetail?.applyCount
                    }
                    recruiterActions={
                      jobDetail?.recruiterActionCount ??
                      jobDetail?.recruiter?.recruiterActions
                    }
                    enableMagicRank={jobDetail?.magicRankFlag}
                    magicSortRank={Math.round(Math.random() * 100)}
                    category={{label: '', tag: ''}}
                    position={
                      !jobDetail?.confidential
                        ? `${jobDetail?.recruiter?.designation} at ${jobDetail?.companyData?.companyName}`
                        : ''
                    }
                    recruiterName={
                      !jobDetail?.confidential
                        ? jobDetail?.recruiter?.recruiterName
                        : 'Verified Recruiter'
                    }
                    showDetails={false}
                    cardTitle="RECRUITER’S ACTIVITY"
                    disableInsights={true}
                    postedOn={moment(jobDetail?.createdTime).toISOString()}
                  />
                </Card>
              )}
            </Grid>
            <Grid item xxs={12} md={8}>
              <Card
                sx={{
                  position: 'relative',
                  height: '100%',
                }}
              >
                <Box
                  display={'flex'}
                  flexWrap={'wrap'}
                  justifyContent={{xxs: 'center', lg: 'space-between'}}
                  rowGap={0.5}
                >
                  <Typography
                    variant="subtitle2"
                    fontSize={{xxs: 10, xs: 12, md: 14}}
                    fontWeight={600}
                  >
                    {chartData?.heading}
                  </Typography>
                  <Box display={'flex'} width={{xxs: 'fit-content'}} gap={3.5}>
                    <Box display={'flex'} alignItems={'center'} gap={0.7}>
                      <Box
                        sx={{
                          width: {xxs: 10, lg: 14},
                          height: {xxs: 10, lg: 14},
                          border: `1px solid #8DD1C0`,
                          background: '#CFFDF1',
                          borderRadius: '100px',
                        }}
                      ></Box>
                      <Typography fontSize={{xxs: 10, lg: 12}} variant="body3">
                        Recruiter’s action
                      </Typography>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} gap={0.7}>
                      <Box
                        sx={{
                          width: {xxs: 10, lg: 14},
                          height: {xxs: 10, lg: 14},
                          border: `1px solid ${
                            isAppIIMJobs ? '#428E76' : theme.palette.insights
                          }`,
                          background: isAppIIMJobs
                            ? '#428E76'
                            : theme.palette.insights,
                          borderRadius: '100px',
                        }}
                      ></Box>
                      <Typography fontSize={{xxs: 10, lg: 12}} variant="body3">
                        {activeTab === 'views'
                          ? 'Total Views'
                          : 'No. of applications'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  px={1.5}
                  py={1}
                  borderRadius={4}
                  border={'1px solid'}
                  borderColor={'border.main'}
                  my={1.5}
                >
                  {chartData && chartData.options && (
                    <Chart
                      type="bar"
                      data={chartData}
                      options={chartData.options}
                      height={widthMap[width] ?? '120px'}
                    />
                  )}
                </Box>
                {activeTab === 'competition' && (
                  <Grid container spacing={1}>
                    <Grid item xxs={12} md={6}>
                      <Box
                        display={'flex'}
                        justifyContent={'space-between'}
                        px={1.5}
                        py={1}
                        height={'100%'}
                        borderRadius={4}
                        border={'1px solid'}
                        borderColor={'border.main'}
                      >
                        <Typography variant="subtitle2">
                          Total no. of Applicants
                        </Typography>
                        <Typography
                          fontSize={{xxs: 24}}
                          fontWeight={700}
                          lineHeight={'24px'}
                          fontFamily={'Abhaya Libre'}
                        >
                          {/* {getArraySum(
                            Object.values(jobDetailInsights?.appliedData || {})
                          )} */}
                          {jobDetailInsights.userAppliedCount}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xxs={12} md={6}>
                      <Box
                        display={'flex'}
                        justifyContent={'space-between'}
                        px={1.5}
                        py={1}
                        height={'100%'}
                        borderRadius={4}
                        border={'1px solid'}
                        borderColor={'border.main'}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            maxWidth: {
                              xxs: '200px',
                              sm: 'max-content',
                              md: '200px',
                              lg: 'max-content',
                            },
                          }}
                        >
                          Total no. of Applicants from top{' '}
                          {isAppIIMJobs ? 'B-School' : 'Engineering Colleges'}
                        </Typography>
                        <Typography
                          fontSize={{xxs: 24}}
                          fontWeight={700}
                          lineHeight={'24px'}
                          fontFamily={'Abhaya Libre'}
                        >
                          {jobDetailInsights?.totalApplicantsFromTopBSchool}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {!insightsAvailable && (
                  <Box
                    sx={{
                      // width: '100%',
                      // height: '100%',
                      background: Gradient,
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: 0,
                      left: 0,
                    }}
                  >
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      height={'100%'}
                      flexDirection={'column'}
                    >
                      <Box width={220} height={150}>
                        <Image
                          src={Insights}
                          alt="Insights"
                          width={224}
                          height={148}
                          layout="responsive"
                          objectFit="cover"
                        />
                      </Box>
                      <NoInsightsMessage isProUser={isProUser} />
                      {!isProUser && (
                        <ButtonV2
                          sx={{
                            background: theme.palette.pro.main,
                            mt: {xxs: theme.spacing(1), md: theme.spacing(2)},
                            py: 1,
                            px: 3,
                            fontSize: {xxs: 12, md: 18},
                          }}
                          variant="contained"
                          size="small"
                          href={getProLink(
                            'jd',
                            jobDetail?.id?.toString(),
                            props?.proMeta?.refCode,
                            props?.proMeta?.refFeature
                          )}
                          target="_self"
                        >
                          Upgrade to PRO
                        </ButtonV2>
                      )}
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>
            {!isLarge && (
              <Grid item xxs={12}>
                <Card sx={{pt: 3, pb: 2, mt: 1}}>
                  <JobOverviewCard
                    variant={VersionVariants.VERSION_TWO}
                    actionUrl={
                      !jobDetail?.confidential
                        ? getOldLink(
                            `/r/${jobDetail?.recruiter
                              ?.recruiterId}-${kebabCase(
                              jobDetail?.recruiter?.recruiterName.replace(
                                /&/g,
                                '-and-'
                              )
                            )}`
                          )
                        : '#'
                    }
                    confidential={jobDetail?.confidential === 1}
                    setOpenConfJobDialog={props?.setOpenConfJobDialog}
                    recruiterImgUrl={
                      !jobDetail?.confidential
                        ? jobDetail?.recruiter?.profilePicUrl
                        : `${getOldLink('/resources/img/user.jpg')}`
                    }
                    lastActive={
                      jobDetail?.recruiter?.lastActive &&
                      jobDetail?.confidential !== 1
                        ? moment(jobDetail.recruiter.lastActive).isValid()
                          ? moment(jobDetail.recruiter.lastActive).format(
                              'DD MMMM YYYY'
                            )
                          : jobDetail.recruiter.lastActive
                        : ''
                    }
                    jobId={jobDetail?.id}
                    jobViews={jobDetail?.hits}
                    applications={
                      jobDetail?.applicationCount ?? jobDetail?.applyCount
                    }
                    recruiterActions={
                      jobDetail?.recruiterActionCount ??
                      jobDetail?.recruiter?.recruiterActions
                    }
                    enableMagicRank={jobDetail?.magicRankFlag}
                    magicSortRank={Math.round(Math.random() * 100)}
                    category={{label: '', tag: ''}}
                    position={
                      !jobDetail?.confidential
                        ? `${jobDetail?.recruiter?.designation} at ${jobDetail?.companyData?.companyName}`
                        : ''
                    }
                    recruiterName={
                      !jobDetail?.confidential
                        ? jobDetail?.recruiter?.recruiterName
                        : 'Verified Recruiter'
                    }
                    showDetails={false}
                    cardTitle="RECRUITER’S ACTIVITY"
                    postedOn={moment(jobDetail?.createdTime).toISOString()}
                  />
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </BootstrapDialog>
    </div>
  );
};

export default Component;
